/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { ExamActions } from "./slices";
import {
  SlicePaginationResponse,
  initSlicePageable,
  IDiagnosticReportContent,
  ContentType,
  DEFAULT_TIMEZONE,
  PaginationResponse,
  IStudyOrderResponse,
  ISeriesDetail,
  prodModeLog,
  getBlobUrlForPDFPreview,
} from "@advapacs/common";
import axios, { Axios } from "axios";
import { AppThunk } from "@src/config/store";
import { seriesAPI, studyOrderAPI } from "@src/config/api";

export interface DownloadStudyRequest {
  studyId: string;
  portalId: string;
}

export const getExamsListing =
  (item): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(ExamActions.getStudiesStart());
      const { page, size = initSlicePageable.size, sort } = item;
      let requestParams = {};
      if (page) requestParams = { ...requestParams, page };
      if (size) requestParams = { ...requestParams, size };
      if (sort) requestParams = { ...requestParams, sort };

      const studies = await axios.get(studyOrderAPI.GET_STUDY_ORDER, {
        params: requestParams,
      });
      dispatch(
        ExamActions.getStudiesSuccess(
          studies.data as SlicePaginationResponse<IStudyOrderResponse>
        )
      );
    } catch (err) {
      dispatch(ExamActions.getStudiesFail(err?.response?.data));
    }
  };

export const getStudyDiagnosticReport =
  (studyId: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await axios.get(
        studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT(studyId)
      );
      dispatch(ExamActions.getReportSuccess(response.data));
    } catch (err) {
      dispatch(ExamActions.getReportFail());
    }
  };

export const studyFetchReportDataContent = async (
  studyId: string,
  selectContent: IDiagnosticReportContent,
  report: boolean
): Promise<string> => {
  let result = "";
  if (selectContent.contentType === ContentType.APPLICATION_PDF) {
    const hasParams = !(!report && selectContent?.tag === ContentType.PDF_TAG);
    const path = !report
      ? studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_CONTENT(
          studyId,
          selectContent.id
        )
      : studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_PDF(
          studyId,
          selectContent.id
        );
    result = await getBlobUrlForPDFPreview(
      hasParams,
      path,
      axios.defaults.baseURL
    );
  } else {
    const response = await axios.get(
      studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_CONTENT(
        studyId,
        selectContent.id
      )
    );
    result = response.data;
  }
  return result;
};

export const downloadStudies =
  (data: DownloadStudyRequest[]): AppThunk =>
  async () => {
    try {
      const urlRefresh = "/auth/refresh";
      await axios.post(urlRefresh);

      const params: Record<string, any> = {
        origin: window.origin,
        studyIds: data.map((d) => d.studyId).toString(),
        portalIds: data.map((d) => d.portalId).toString(),
        "x-tz": DEFAULT_TIMEZONE.value,
      };

      const urlParams = new URLSearchParams(params);
      const SERVER_API_URL = axios.defaults.baseURL;

      window.open(
        `${SERVER_API_URL}/studies/download?${urlParams.toString()}`,
        "_blank"
      );
    } catch (err) {}
  };

export const studyDownloadPdf = async (
  studyId: string,
  contentId: string,
  report: boolean
) => {
  const url = !report
    ? studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_CONTENT(
        studyId,
        contentId
      )
    : studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_PDF(studyId, contentId);
  const params: Record<string, string> = {
    origin: window.origin,
    download: "true",
    "x-tz": DEFAULT_TIMEZONE.value,
  };
  const urlParams = new URLSearchParams(params);
  const SERVER_API_URL = axios.defaults.baseURL;
  window.open(`${SERVER_API_URL}${url}?${urlParams.toString()}`, "_blank");
};

export const createHttpRequestAsyncThunk = (
  actionType: string,
  apiUrl: string,
  config?: {
    getAxiosInstance?: () => Axios;
    method?: string;
  }
) =>
  createAsyncThunk(
    actionType,
    async (
      data: {
        payload: any;
        pathVariables: { [key: string]: string };
      },
      { rejectWithValue }
    ) => {
      try {
        if ((data as unknown as any)?.pathVariables) {
          const pathVariables = (data as unknown as any)?.pathVariables;
          Object.keys(pathVariables).forEach((key) => {
            apiUrl = apiUrl.replace(`{${key}}`, pathVariables[key]);
          });
          data = (data as any).payload;
        }
        const response = await (config?.getAxiosInstance
          ? config?.getAxiosInstance()
          : axios
        ).request({
          url: apiUrl,
          method: config?.method || "GET",
          data,
        });
        return response.data;
      } catch (err) {
        return rejectWithValue(err?.response?.data);
      }
    }
  );

export const getStudyDetail =
  (studyId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(ExamActions.getStudyOrOrderDetailStart());

      const study = await axios.get(
        studyOrderAPI.GET_STUDY_ORDER_DETAIL(studyId)
      );
      dispatch(
        ExamActions.getStudyOrOrderDetailSuccess({
          ...study.data,
          isStudy: true,
        })
      );
    } catch (err) {
      dispatch(ExamActions.getStudyOrOrderDetailFail());
    }
  };

export const studyDownloadPdfReport = async (
  studyId: string,
  contentId: string,
  report: boolean
) => {
  const url = report
    ? studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_PDF(studyId, contentId)
    : studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT_CONTENT(
        studyId,
        contentId
      );
  const params: Record<string, string> = {
    origin: window.origin,
    download: "true",
    "x-tz": DEFAULT_TIMEZONE.value,
  };
  const urlParams = new URLSearchParams(params);
  const SERVER_API_URL = axios.defaults.baseURL;
  window.open(`${SERVER_API_URL}${url}?${urlParams.toString()}`, "_blank");
};

export const getAllSeries =
  (studyId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(ExamActions.getAllSeriesStart());

      const series = await axios.get(
        `${seriesAPI.GET_SERIES(studyId)}?idOnly=true&size=1000000`
      );
      dispatch(
        ExamActions.getAllSeriesSuccess(
          series.data as PaginationResponse<ISeriesDetail>
        )
      );
    } catch (err) {
      dispatch(ExamActions.getAllSeriesFail());
    }
  };

export const downloadSeries =
  (studyId: string, seriesId: string): AppThunk =>
  async () => {
    await axios.post(`/auth/refresh`);

    const params: Record<string, any> = {
      origin: window.origin,
      uncompressed: true,
      "x-tz": DEFAULT_TIMEZONE.value,
    };
    const urlParams = new URLSearchParams(params);
    const SERVER_API_URL = axios.defaults.baseURL;
    window.open(
      `${SERVER_API_URL}/studies/${studyId}/series/${seriesId}/download/zip?${urlParams.toString()}`,
      `download-series-${seriesId}`
    );
  };

export const downloadFile =
  (subUrl: string): AppThunk =>
  async () => {
    await axios.post(`/auth/refresh`);
    const params: Record<string, string> = {
      origin: window.origin,
      "x-tz": DEFAULT_TIMEZONE.value,
    };
    const urlParams = new URLSearchParams(params);
    const SERVER_API_URL = axios.defaults.baseURL;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const appendParamsToUrl = (url, params) =>
      url + (url.includes("?") ? "&" : "?") + params.toString();

    const urlDownloaded = `${SERVER_API_URL}${appendParamsToUrl(
      subUrl,
      urlParams
    )}`;

    prodModeLog(urlDownloaded);
    window.open(urlDownloaded, "_blank");
  };
